import type { ScanRequest, ScanResponse } from '@pl/domain';
import { ApiError, NetworkError } from '../../lib/api';
import { loadScans, type QueuedScan, removeScan, saveScan } from './store';

export type SubmitResult = { state: 'done'; response: ScanResponse } | { state: 'queued' } | { state: 'failed'; error: ApiError };

/** Then 30 s repeatedly. Long enough not to hammer a dead dock, short enough to catch a gap. */
export const DEFAULT_RETRY_MS = [2000, 5000, 10_000, 30_000];
const SETTLED_TTL_MS = 24 * 60 * 60 * 1000;
/**
 * How many answered failures a single scan may collect before it stops holding the line. Only
 * failures the server itself produced count (see `#attempt`), so a dead network never trips it.
 */
const DEFAULT_MAX_HARD_FAILURES = 5;

/**
 * The thing that decides whether forty scans behind a stack of pallets are still forty scans an
 * hour later.
 *
 * Every scan is written to IndexedDB before it is sent and only ever leaves in one of two ways:
 * the server answered, or the worker acknowledged a refusal. There is no path that drops a scan
 * because a request failed. Retries always resend the original `clientScanId`, so the server's
 * idempotency turns a duplicate delivery into a replayed answer rather than a second box.
 */
export class ScanQueue {
  readonly #send: (req: ScanRequest) => Promise<ScanResponse>;
  readonly #retryMs: number[];
  readonly #maxHardFailures: number;
  /** Called when a scan the worker has already walked away from comes back refused. */
  readonly #onLateRefusal: () => void;
  readonly #items = new Map<string, QueuedScan>();
  readonly #listeners = new Set<() => void>();
  readonly #inFlight = new Set<string>();
  /** Scans that are in memory but not on disk, because a write failed. */
  readonly #unsaved = new Set<string>();
  readonly #onOnline = () => void this.flush();
  #version = 0;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #flushing = false;
  #draining = false;
  #disposed = false;
  #storageOk = true;
  /** Keeps `createdAt` strictly increasing, so two scans in the same millisecond still have an order. */
  #lastCreatedAt = 0;

  constructor(send: (req: ScanRequest) => Promise<ScanResponse>, opts?: { retryMs?: number[]; maxHardFailures?: number; onLateRefusal?: () => void }) {
    this.#send = send;
    this.#retryMs = opts?.retryMs?.length ? [...opts.retryMs] : DEFAULT_RETRY_MS;
    this.#maxHardFailures = opts?.maxHardFailures ?? DEFAULT_MAX_HARD_FAILURES;
    this.#onLateRefusal = opts?.onLateRefusal ?? (() => undefined);
    this.#listen();
  }

  /** Loads whatever the last session left behind, clears what is settled and old, and sends the rest. */
  async hydrate(): Promise<void> {
    this.#disposed = false;
    this.#listen();
    let stored: QueuedScan[] = [];
    try {
      stored = await loadScans();
      // Anything this session could not write is still only in memory; reading is not saving.
      this.#storageOk = this.#unsaved.size === 0;
    } catch {
      this.#storageOk = false;
    }
    const cutoff = Date.now() - SETTLED_TTL_MS;
    for (const item of stored) {
      if (item.status !== 'pending' && !item.needsAttention && item.createdAt < cutoff) {
        void removeScan(item.clientScanId).catch(() => undefined);
        continue;
      }
      // A row written before `hardFailures` existed simply starts its count now.
      this.#items.set(item.clientScanId, { ...item, hardFailures: item.hardFailures ?? 0 });
      this.#lastCreatedAt = Math.max(this.#lastCreatedAt, item.createdAt);
    }
    this.#bump();
    await this.flush();
  }

  /**
   * Writes the scan down, then tries to send it. The return value is what the worker is told, so
   * it is only ever `done` when the server has actually answered.
   */
  async submit(req: ScanRequest, context: string): Promise<SubmitResult> {
    const createdAt = Math.max(Date.now(), this.#lastCreatedAt + 1);
    this.#lastCreatedAt = createdAt;
    const item: QueuedScan = {
      clientScanId: req.clientScanId,
      request: req,
      context,
      status: 'pending',
      response: null,
      error: null,
      attempts: 0,
      hardFailures: 0,
      createdAt,
      needsAttention: false,
    };
    this.#items.set(item.clientScanId, item);
    await this.#persist(item);
    this.#bump();
    return this.#attempt(item, true);
  }

  /**
   * Oldest first, stopping at the first one that still cannot go — nothing overtakes a scan that
   * is only waiting for a signal. The one exception is a scan that has been set aside after
   * repeatedly failing against a server that was answering: it stays queued and keeps being
   * retried, but it no longer holds every other job on the phone behind it.
   */
  async flush(): Promise<void> {
    if (this.#flushing) return;
    this.#flushing = true;
    try {
      for (const item of this.#pendingInOrder()) {
        if (item.status !== 'pending') continue;
        await this.#attempt(item, false);
        if (item.status === 'pending' && !item.needsAttention) break;
      }
    } finally {
      this.#flushing = false;
    }
  }

  /** One scan as it stands right now — a screen reads this to keep its own counters honest. */
  get(clientScanId: string): QueuedScan | undefined {
    return this.#items.get(clientScanId);
  }

  pending(context?: string): number {
    let n = 0;
    for (const item of this.#items.values()) if (item.status === 'pending' && (context === undefined || item.context === context)) n += 1;
    return n;
  }

  /**
   * Scans the server has answered, accepted or refused. Within a session this only ever rises, so
   * a screen that watches it learns that one of its scans has landed — including one that landed
   * in a flush nobody was looking at, which is the one that leaves a screen showing old numbers.
   */
  settled(context?: string): number {
    let n = 0;
    for (const item of this.#items.values()) if (item.status !== 'pending' && (context === undefined || item.context === context)) n += 1;
    return n;
  }

  /** How many are on the wire right now — the difference between "waiting" and "going". */
  sending(): number {
    return this.#inFlight.size;
  }

  /** Bad answers that landed after the worker had been told the scan was safe. */
  attention(): QueuedScan[] {
    return [...this.#items.values()].filter((i) => i.needsAttention).sort((a, b) => a.createdAt - b.createdAt);
  }

  /**
   * False from the moment a write to IndexedDB fails until every scan it skipped has been written
   * — the screen must stop promising "saved on this phone" for as long as one of them is RAM only.
   */
  storageOk(): boolean {
    return this.#storageOk;
  }

  async acknowledge(clientScanId: string): Promise<void> {
    const item = this.#items.get(clientScanId);
    if (!item?.needsAttention) return;
    item.needsAttention = false;
    await this.#persist(item);
    this.#bump();
  }

  subscribe(fn: () => void): () => void {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  }

  version(): number {
    return this.#version;
  }

  /** Stops the timers and the online listener. Anything already on the wire is still allowed to land. */
  dispose(): void {
    this.#disposed = true;
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
    if (typeof window !== 'undefined') window.removeEventListener('online', this.#onOnline);
  }

  #listen(): void {
    if (typeof window === 'undefined') return;
    window.removeEventListener('online', this.#onOnline);
    window.addEventListener('online', this.#onOnline);
  }

  #pendingInOrder(): QueuedScan[] {
    return [...this.#items.values()].filter((i) => i.status === 'pending').sort((a, b) => a.createdAt - b.createdAt);
  }

  /**
   * `live` means a worker is watching this attempt and will be shown its outcome. A bad answer to
   * an attempt nobody is watching is the dangerous one: it becomes `needsAttention` so the phone
   * can raise it later, instead of disappearing into the log.
   */
  async #attempt(item: QueuedScan, live: boolean): Promise<SubmitResult> {
    item.attempts += 1;
    this.#inFlight.add(item.clientScanId);
    this.#bump();
    try {
      const response = await this.#send(item.request);
      item.status = 'done';
      item.response = response;
      item.error = null;
      if (!live && response.outcome === 'rejected') {
        item.needsAttention = true;
        this.#onLateRefusal();
      }
      await this.#persist(item);
      return { state: 'done', response };
    } catch (err) {
      // Below 500 the server has made a decision about this request; sending it again changes
      // nothing. Everything else — no connection, a 5xx, an unexpected throw — is the road being
      // out, and the scan waits.
      if (err instanceof ApiError && err.status < 500) {
        item.status = 'failed';
        item.error = err.message;
        if (!live) {
          item.needsAttention = true;
          this.#onLateRefusal();
        }
        await this.#persist(item);
        return { state: 'failed', error: err };
      }
      item.status = 'pending';
      item.error = err instanceof Error ? err.message : 'No connection to the server';
      // A dead network is not this scan's fault: behind a stack of pallets every scan fails, and
      // setting forty of them aside would bury the one that is genuinely poisoned. Only a failure
      // the server itself produced — a 5xx, or an unexpected throw — counts against the scan, and
      // once it has collected enough of them it stops holding the rest of the phone hostage.
      if (!(err instanceof NetworkError)) {
        item.hardFailures += 1;
        if (!live && item.hardFailures >= this.#maxHardFailures && !item.needsAttention) {
          item.needsAttention = true;
          item.error = `${item.error} — still not sent after ${item.attempts} tries.`;
        }
      }
      await this.#persist(item);
      this.#schedule();
      return { state: 'queued' };
    } finally {
      this.#inFlight.delete(item.clientScanId);
      this.#bump();
    }
  }

  #schedule(): void {
    if (this.#disposed) return;
    const head = this.#pendingInOrder()[0];
    if (!head) return;
    if (this.#timer !== null) clearTimeout(this.#timer);
    const delay = this.#retryMs[Math.min(Math.max(head.attempts - 1, 0), this.#retryMs.length - 1)]!;
    this.#timer = setTimeout(() => {
      this.#timer = null;
      void this.flush();
    }, delay);
  }

  async #persist(item: QueuedScan): Promise<void> {
    try {
      await saveScan({ ...item });
      this.#unsaved.delete(item.clientScanId);
    } catch {
      // The scan is still in memory and will still be retried; what is lost is surviving a reload,
      // and the screen is told so rather than promising the phone has it.
      this.#unsaved.add(item.clientScanId);
      this.#storageOk = false;
      return;
    }
    await this.#drainUnsaved();
    // One write getting through says nothing about the scans that were skipped while storage was
    // failing: only the head of the queue is ever retried, so nothing else would rewrite them.
    // Until they are all down, the phone is not holding the queue and must not claim it is.
    this.#storageOk = this.#unsaved.size === 0;
  }

  /** Rewrites what storage refused earlier, riding on a write that has just proved it can. */
  async #drainUnsaved(): Promise<void> {
    if (this.#draining || this.#unsaved.size === 0) return;
    this.#draining = true;
    try {
      for (const id of [...this.#unsaved]) {
        const item = this.#items.get(id);
        if (!item) {
          this.#unsaved.delete(id);
          continue;
        }
        try {
          await saveScan({ ...item });
          this.#unsaved.delete(id);
        } catch {
          break; // storage is refusing again; the rest stay marked, and so does the warning
        }
      }
    } finally {
      this.#draining = false;
    }
  }

  #bump(): void {
    this.#version += 1;
    for (const fn of [...this.#listeners]) fn();
  }
}
